import axios from 'axios';

const DESKTOP_API_BASE_URL =
  typeof window !== 'undefined' && window.desktopConfig?.apiBaseUrl
    ? window.desktopConfig.apiBaseUrl
    : null;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || DESKTOP_API_BASE_URL || '/api';
const AUTH_TOKEN_STORAGE_KEY = 'szkody.auth.token';

const getStoredAuthToken = () => window.localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);

export const saveAuthToken = (token) => {
  if (token) {
    window.localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
  }
};

export const clearAuthToken = () => {
  window.localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getStoredAuthToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const login = async (username, password) => {
  try {
    const response = await api.post('/auth/login', { username, password });
    const token = response?.data?.token;
    if (token) {
      saveAuthToken(token);
    }
    return response.data;
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await api.get('/auth/me');
    return response.data;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

export const listOrganizations = async () => {
  try {
    const response = await api.get('/auth/organizations');
    return response.data;
  } catch (error) {
    console.error('Error fetching organizations:', error);
    throw error;
  }
};

export const hasAuthToken = () => !!getStoredAuthToken();

export const getFields = async () => {
  try {
    const response = await api.get('/fields');
    return response.data;
  } catch (error) {
    console.error('Error fetching fields:', error);
    throw error;
  }
};

export const createField = async (fieldData) => {
  try {
    const response = await api.post('/fields', fieldData);
    return response.data;
  } catch (error) {
    console.error('Error creating field:', error);
    throw error;
  }
};

export const updateField = async (fieldId, fieldData) => {
  try {
    const response = await api.patch(`/fields/${encodeURIComponent(fieldId)}`, fieldData);
    return response.data;
  } catch (error) {
    console.error('Error updating field:', error);
    throw error;
  }
};

export const deleteField = async (fieldId) => {
  try {
    const response = await api.delete(`/fields/${encodeURIComponent(fieldId)}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting field:', error);
    throw error;
  }
};

export const healthCheck = async () => {
  try {
    const response = await api.get('/health');
    return response.data;
  } catch (error) {
    console.error('Error checking health:', error);
    throw error;
  }
};

export const uploadGeoTIFF = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await api.post('/upload-geotiff', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading GeoTIFF:', error);
    throw error;
  }
};

export const getGeoTIFFBlob = async (filename) => {
  try {
    const response = await api.get(`/geotiff/${encodeURIComponent(filename)}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading GeoTIFF:', error);
    throw error;
  }
};

export const getGeoTIFFUrl = (filename) => `${API_BASE_URL}/geotiff/${encodeURIComponent(filename)}`;

export const getGeoTIFFHeaders = () => {
  const token = getStoredAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getGeoTIFFMetadata = async (filename) => {
  try {
    const response = await api.get(`/geotiff/${encodeURIComponent(filename)}/metadata`, { timeout: 30000 });
    return response.data;
  } catch (error) {
    console.error('Error loading GeoTIFF metadata:', error);
    throw error;
  }
};

export const getGeoTIFFAoi = async (filename) => {
  try {
    const response = await api.get(`/geotiff/${encodeURIComponent(filename)}/aoi`);
    return response.data;
  } catch (error) {
    console.error('Error preparing GeoTIFF AOI:', error);
    throw error;
  }
};

export const getGeoTIFFTile = async (filename, version, { z, x, y }, signal) => {
  const response = await api.get(`/geotiff/${encodeURIComponent(filename)}/tiles/${z}/${x}/${y}.png`, {
    params: { v: version },
    responseType: 'blob',
    timeout: 60000,
    signal,
  });
  return response.data;
};

export default api;
